<template>
  <div class="wishlist-item">
    <div class="item-header">
      <h3 class="item-name">{{ item.name }}</h3>
      <div class="item-actions">
        <!-- Actions for own items -->
        <template v-if="isOwnItem">
          <button @click="$emit('edit', item)" class="edit-btn" title="Redigera">
            ✏️
          </button>
          <button @click="handleDelete" class="delete-btn" title="Ta bort" :disabled="deleting">
            <span v-if="deleting">⏳</span>
            <span v-else>🗑️</span>
          </button>
        </template>
        
        <!-- Actions for other users' items -->
        <template v-else-if="isAuthenticated">
          <button 
            @click="handleAssign" 
            class="assign-btn" 
            :class="{ 'assigned': isAssignedByCurrentUser }"
            :title="isAssignedByCurrentUser ? 'Uppdatera assignment' : 'Assigna dig till detta item'"
          >
            <span v-if="isAssignedByCurrentUser">✅</span>
            <span v-else>🎯</span>
          </button>
        </template>
      </div>
    </div>
    
    <div class="item-content">
      <p class="item-description">{{ item.description }}</p>
      
      <div class="item-details">
        <div class="priority-badge" :class="priorityClass">
          Prioritet: {{ item.priority }}
        </div>
        
        <div v-if="item.price" class="price">
          {{ formatPrice(item.price) }} SEK
        </div>
      </div>
      
            <!-- Assignments (only shown for other users' items when authenticated) -->
      <div v-if="!isOwnItem && isAuthenticated && item.assignments && item.assignments.length > 0" class="assignments">
        <div class="assignment-tags">
          <div 
            v-for="assignment in item.assignments" 
            :key="assignment.id" 
            class="assignment-tag"
            :class="{ 'own-assignment': assignment.assignee.id === currentUserId }"
            @click="toggleCommentModal(assignment)"
          >
            <span class="assignee-name">{{ assignment.assignee.name }}</span>
            <span v-if="assignment.comment" class="comment-icon" @click.stop="toggleCommentModal(assignment)">
              ℹ️
            </span>
            
            <!-- Actions for own assignments -->
            <div v-if="assignment.assignee.id === currentUserId" class="assignment-actions">
              <button 
                @click.stop="$emit('updateAssignment', assignment)" 
                class="update-assignment-btn"
                :title="'Uppdatera kommentar'"
              >
                ✏️
              </button>
              <button 
                @click.stop="handleRemoveAssignment(assignment.id)" 
                class="remove-assignment-btn"
                :title="'Ta bort assignment'"
                :disabled="removingAssignment"
              >
                <span v-if="removingAssignment">⏳</span>
                <span v-else>🗑️</span>
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <a :href="item.url" target="_blank" rel="noopener noreferrer" class="item-link">
        Visa produkt 🔗
      </a>
    </div>

    <!-- Comment Modal for Touch Devices -->
    <div v-if="showCommentModal" class="comment-modal-overlay" @click="closeCommentModal">
      <div class="comment-modal" @click.stop>
        <div class="comment-modal-header">
          <h4>{{ selectedAssignment?.assignee.name }}s kommentar</h4>
          <button @click="closeCommentModal" class="modal-close-btn">×</button>
        </div>
        <div class="comment-modal-content">
          <p v-if="selectedAssignment?.comment" class="comment-text">
            {{ selectedAssignment.comment }}
          </p>
          <p v-else class="no-comment">
            Ingen kommentar angiven
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiService from '@/services/api.js';
import authService from '@/services/auth.js';

export default {
  name: 'WishListItem',
  emits: ['edit', 'delete', 'assign', 'updateAssignment', 'removeAssignment'],
  props: {
    item: {
      type: Object,
      required: true
    },
    currentUserId: {
      type: String,
      default: null
    },
    isOwnItem: {
      type: Boolean,
      default: false
    },
    isAuthenticated: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      deleting: false,
      removingAssignment: false,
      showCommentModal: false,
      selectedAssignment: null
    };
  },
  computed: {
    priorityClass() {
      if (this.item.priority <= 3) return 'priority-high';
      if (this.item.priority <= 6) return 'priority-medium';
      return 'priority-low';
    },
    
    isAssignedByCurrentUser() {
      if (!this.currentUserId || !this.item.assignments) return false;
      return this.item.assignments.some(
        assignment => assignment.assignee.id === this.currentUserId
      );
    }
  },
  methods: {
    formatPrice(price) {
      return new Intl.NumberFormat('sv-SE', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(price);
    },
    
    async handleDelete() {
      if (!confirm(`Är du säker på att du vill ta bort "${this.item.name}"?`)) {
        return;
      }
      
      this.deleting = true;
      
      try {
        const token = authService.getToken();
        if (!token) {
          throw new Error('Du måste vara inloggad');
        }
        
        await apiService.deleteItem(this.item.id, token);
        this.$emit('delete', this.item.id);
        
      } catch (error) {
        alert('Kunde inte ta bort objektet: ' + error.message);
      } finally {
        this.deleting = false;
      }
    },
    
    handleAssign() {
      this.$emit('assign', this.item);
    },
    
    async handleRemoveAssignment(assignmentId) {
      if (!confirm('Är du säker på att du vill ta bort din assignment?')) {
        return;
      }
      
      this.removingAssignment = true;
      
      try {
        this.$emit('removeAssignment', assignmentId);
      } catch (error) {
        console.error('Error removing assignment:', error);
      } finally {
        this.removingAssignment = false;
      }
    },

    toggleCommentModal(assignment) {
      if (assignment.comment) {
        this.selectedAssignment = assignment;
        this.showCommentModal = true;
      }
    },

    closeCommentModal() {
      this.showCommentModal = false;
      this.selectedAssignment = null;
    }
  }
};
</script>

<style scoped>
.wishlist-item {
  background: white;
  border: 2px solid #e9ecef;
  border-radius: 10px;
  padding: 1.5rem;
  margin-bottom: 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  position: relative;
}

.wishlist-item:hover {
  border-color: rgba(46, 125, 50, 0.4);
  box-shadow: 0 8px 22px rgba(46, 125, 50, 0.12);
}

.item-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
  gap: 1rem;
}

.item-name {
  margin: 0;
  font-size: 1.25rem;
  color: #333;
  flex: 1;
  word-break: break-word;
}

.item-actions {
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
}

.edit-btn,
.delete-btn,
.assign-btn,
.update-assignment-btn,
.remove-assignment-btn {
  background: none;
  border: 2px solid #ddd;
  border-radius: 50%;
  width: 44px;
  height: 44px;
  cursor: pointer;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

.edit-btn:hover,
.edit-btn:focus {
  border-color: #2e7d32;
  background-color: #f8f9fa;
  outline: none;
}

.delete-btn:hover:not(:disabled),
.delete-btn:focus:not(:disabled) {
  border-color: #dc3545;
  background-color: #f8f9fa;
  outline: none;
}

.delete-btn:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.assign-btn {
  border-color: #28a745;
}

.assign-btn:hover,
.assign-btn:focus {
  background-color: #e8f5e8;
  outline: none;
}

.assign-btn.assigned {
  background-color: #28a745;
  color: white;
}

.assign-btn.assigned:hover,
.assign-btn.assigned:focus {
  background-color: #218838;
}

.item-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.item-description {
  margin: 0;
  color: #666;
  line-height: 1.5;
  word-break: break-word;
}

.item-details {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
}

.priority-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
  color: white;
  white-space: nowrap;
}

.priority-high {
  background-color: #dc3545;
}

.priority-medium {
  background-color: #ffc107;
  color: #333;
}

.priority-low {
  background-color: #28a745;
}

.price {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1b5e20;
  white-space: nowrap;
}

.assignments {
  margin-top: 0.5rem;
}

.assignment-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.assignment-tag {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  border: 1px solid #2196f3;
  border-radius: 25px;
  font-size: 0.9rem;
  color: #1976d2;
  transition: all 0.3s ease;
  cursor: pointer;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  min-height: 44px;
  position: relative;
}

.assignment-tag.own-assignment {
  background: linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%);
  border-color: #4caf50;
  color: #2e7d32;
  font-weight: 600;
}

.assignment-tag:hover,
.assignment-tag:focus {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  outline: none;
}

.assignment-tag:active {
  transform: translateY(0);
}

.assignee-name {
  font-weight: 600;
  white-space: nowrap;
}

.comment-icon {
  font-size: 1rem;
  cursor: pointer;
  opacity: 0.8;
  padding: 0.25rem;
  border-radius: 50%;
  transition: all 0.3s ease;
  touch-action: manipulation;
}

.comment-icon:hover,
.comment-icon:focus {
  background-color: rgba(255, 255, 255, 0.8);
  opacity: 1;
  transform: scale(1.1);
}

.assignment-actions {
  display: flex;
  gap: 0.25rem;
  margin-left: 0.5rem;
}

.update-assignment-btn {
  width: 36px;
  height: 36px;
  font-size: 0.8rem;
  border-color: #007BFF;
}

.update-assignment-btn:hover,
.update-assignment-btn:focus {
  background-color: #e7f3ff;
  outline: none;
}

.remove-assignment-btn {
  width: 36px;
  height: 36px;
  font-size: 0.8rem;
  border-color: #dc3545;
}

.remove-assignment-btn:hover:not(:disabled),
.remove-assignment-btn:focus:not(:disabled) {
  background-color: #ffe6e6;
  outline: none;
}

.remove-assignment-btn:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.item-link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: #1b5e20;
  text-decoration: none;
  font-weight: 700;
  padding: 0.75rem 1rem;
  border: 2px solid #2e7d32;
  border-radius: 999px;
  transition: all 0.25s ease;
  align-self: flex-start;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  min-height: 44px;
}

.item-link:hover,
.item-link:focus {
  background-color: #2e7d32;
  color: white;
  outline: none;
}

/* Comment Modal Styles */
.comment-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 1rem;
  box-sizing: border-box;
}

.comment-modal {
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
  width: 100%;
  max-width: 400px;
  max-height: 80vh;
  overflow: hidden;
  animation: modalFadeIn 0.3s ease;
}

@keyframes modalFadeIn {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.comment-modal-header {
  background: #f8f9fa;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #dee2e6;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.comment-modal-header h4 {
  margin: 0;
  color: #333;
  font-size: 1.1rem;
}

.modal-close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
  padding: 0.5rem;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  touch-action: manipulation;
}

.modal-close-btn:hover,
.modal-close-btn:focus {
  background: #e9ecef;
  outline: none;
}

.comment-modal-content {
  padding: 1.5rem;
}

.comment-text {
  margin: 0;
  color: #333;
  line-height: 1.6;
  font-size: 1rem;
  word-break: break-word;
}

.no-comment {
  margin: 0;
  color: #666;
  font-style: italic;
  text-align: center;
}

/* Mobile responsive styles */
@media (max-width: 768px) {
  .wishlist-item {
    padding: 1rem;
    margin-bottom: 0.75rem;
  }

  .item-header {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }
  
  .item-name {
    font-size: 1.1rem;
    margin-bottom: 0.5rem;
  }
  
  .item-actions {
    align-self: flex-end;
    gap: 0.75rem;
  }

  .edit-btn,
  .delete-btn,
  .assign-btn {
    width: 48px;
    height: 48px;
    font-size: 1.1rem;
  }
  
  .item-details {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
  
  .assignment-tags {
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .assignment-tag {
    justify-content: space-between;
    width: 100%;
    padding: 1rem 1.25rem;
    min-height: 56px;
  }

  .assignment-actions {
    margin-left: 0;
  }

  .update-assignment-btn,
  .remove-assignment-btn {
    width: 44px;
    height: 44px;
    font-size: 1rem;
  }

  .item-link {
    align-self: stretch;
    justify-content: center;
    padding: 1rem;
    font-size: 1rem;
  }

  .comment-modal {
    margin: 1rem;
    max-height: 90vh;
  }

  .comment-modal-header {
    padding: 1rem 1.25rem;
  }

  .comment-modal-content {
    padding: 1.25rem;
  }
}

@media (max-width: 480px) {
  .wishlist-item {
    padding: 0.75rem;
  }

  .item-name {
    font-size: 1rem;
  }

  .edit-btn,
  .delete-btn,
  .assign-btn {
    width: 44px;
    height: 44px;
    font-size: 1rem;
  }

  .assignment-tag {
    padding: 0.875rem 1rem;
    font-size: 0.85rem;
  }

  .comment-modal {
    margin: 0.5rem;
  }
}

/* Touch device improvements */
@media (hover: none) and (pointer: coarse) {
  .wishlist-item:hover {
    border-color: #e9ecef;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  .assignment-tag:hover {
    transform: none;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  .edit-btn:hover,
  .delete-btn:hover,
  .assign-btn:hover,
  .update-assignment-btn:hover,
  .remove-assignment-btn:hover,
  .item-link:hover {
    transform: none;
  }

  /* Add visual feedback for taps */
  .assignment-tag:active {
    background: linear-gradient(135deg, #d1ecf1 0%, #a6d4fa 100%);
  }

  .assignment-tag.own-assignment:active {
    background: linear-gradient(135deg, #d4edda 0%, #b6e2c1 100%);
  }
}
</style>
